import { postJson } from '../../composables/useApi';
import FiltersRangeSliders from './FiltersRangeSliders';

const DEFAULT_ITEMS_PER_PAGE = 20;
const DEFAULT_SORT_FIELD = 'date';
const DEFAULT_SORT_DIRECTION = 'desc';

interface OffersFiltersPayload {
  searchQuery?: string;
  categoryId?: number;
  selectedFeatureIds?: number[];
  minPrice?: number;
  maxPrice?: number;
  sortField?: string;
  sortDirection?: string;
  currentPage?: number;
  itemsPerPage?: number;
  sellerId?: number;
}

const extractNumericValue = (formData: FormData, fieldName: string): number | undefined => {
  const value = formData.get(fieldName);
  
  if (value === null || value === '' || value === '0') {
    return undefined;
  }
  
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? undefined : numericValue;
};


const extractSelectedFeatureIds = (formData: FormData): number[] => {
  const selectedFeatureIds: number[] = [];
  
  formData.forEach((value, key) => {
    if (key === 'features[]') {
      const featureId = Number(value);
      if (!Number.isNaN(featureId)) {
        selectedFeatureIds.push(featureId);
      }
    }
  });
  
  return selectedFeatureIds;
};


const readFiltersFromSidebarForm = (sidebarForm: HTMLFormElement): OffersFiltersPayload => {
  const formData = new FormData(sidebarForm);
  const filtersPayload: OffersFiltersPayload = {};

  const searchQuery = (formData.get('q') as string) || '';
  if (searchQuery.trim() !== '') {
    filtersPayload.searchQuery = searchQuery.trim();
  }

  const categoryId = extractNumericValue(formData, 'category');
  if (categoryId !== undefined) {
    filtersPayload.categoryId = categoryId;
  }

  const selectedFeatureIds = extractSelectedFeatureIds(formData);
  if (selectedFeatureIds.length > 0) {
    filtersPayload.selectedFeatureIds = selectedFeatureIds;
  }

  const minPrice = extractNumericValue(formData, 'min_price');
  if (minPrice !== undefined) {
    filtersPayload.minPrice = minPrice;
  }

  const maxPrice = extractNumericValue(formData, 'max_price');
  if (maxPrice !== undefined) {
    filtersPayload.maxPrice = maxPrice;
  }

  const sellerId = extractNumericValue(formData, 'seller');
  if (sellerId !== undefined) {
    filtersPayload.sellerId = sellerId;
  }

  return filtersPayload;
};


const readSortingPreferencesFromForm = (sortingForm: HTMLFormElement): Partial<OffersFiltersPayload> => {
  const formData = new FormData(sortingForm);
  
  const sortField = (formData.get('sort') as string) || DEFAULT_SORT_FIELD;
  const sortDirection = (formData.get('direction') as string) || DEFAULT_SORT_DIRECTION;
  
  return { 
    sortField, 
    sortDirection 
  };
};

const convertFiltersToBackendFormat = (filtersPayload: OffersFiltersPayload): Record<string, any> => {
  return {
    q: filtersPayload.searchQuery,
    category: filtersPayload.categoryId,
    features: filtersPayload.selectedFeatureIds,
    min_price: filtersPayload.minPrice,
    max_price: filtersPayload.maxPrice,
    sort: filtersPayload.sortField || DEFAULT_SORT_FIELD,
    direction: filtersPayload.sortDirection || DEFAULT_SORT_DIRECTION,
    page: filtersPayload.currentPage || 1,
    limit: filtersPayload.itemsPerPage || DEFAULT_ITEMS_PER_PAGE,
    seller: filtersPayload.sellerId,
  };
};


const fetchFilteredOffersFromServer = async (apiUrl: string, filtersPayload: OffersFiltersPayload): Promise<string> => {
  const backendPayload = convertFiltersToBackendFormat(filtersPayload);
  const response = await postJson<{ success: boolean; html: string }>(apiUrl, backendPayload);
  
  if (!response || !response.success) {
    throw new Error('Failed to fetch filtered offers from server');
  }
  
  return response.html;
};


const buildUrlWithCleanParameters = (baseUrl: string, filtersPayload: OffersFiltersPayload): string => {
  const url = new URL(baseUrl, window.location.origin);
  const urlSearchParams = url.searchParams;

  if (filtersPayload.searchQuery) {
    urlSearchParams.set('q', filtersPayload.searchQuery);
  } else {
    urlSearchParams.delete('q');
  }

  if (filtersPayload.categoryId) {
    urlSearchParams.set('category', String(filtersPayload.categoryId));
  } else {
    urlSearchParams.delete('category');
  }

  if (filtersPayload.sortField && filtersPayload.sortField !== DEFAULT_SORT_FIELD) {
    urlSearchParams.set('sort', filtersPayload.sortField);
  } else {
    urlSearchParams.delete('sort');
  }
  
  if (filtersPayload.sortDirection && filtersPayload.sortDirection !== DEFAULT_SORT_DIRECTION) {
    urlSearchParams.set('direction', filtersPayload.sortDirection);
  } else {
    urlSearchParams.delete('direction');
  }

  if (filtersPayload.currentPage && filtersPayload.currentPage > 1) {
    urlSearchParams.set('page', String(filtersPayload.currentPage));
  } else {
    urlSearchParams.delete('page');
  }

  if (filtersPayload.itemsPerPage && filtersPayload.itemsPerPage !== DEFAULT_ITEMS_PER_PAGE) {
    urlSearchParams.set('limit', String(filtersPayload.itemsPerPage));
  } else {
    urlSearchParams.delete('limit');
  }

  urlSearchParams.delete('min_price');
  urlSearchParams.delete('max_price');
  
  if (filtersPayload.minPrice !== undefined && filtersPayload.minPrice > 0) {
    urlSearchParams.set('min_price', String(filtersPayload.minPrice));
  }
  
  if (filtersPayload.maxPrice !== undefined && filtersPayload.maxPrice > 0) {
    urlSearchParams.set('max_price', String(filtersPayload.maxPrice));
  }

  urlSearchParams.delete('features[]');
  if (filtersPayload.selectedFeatureIds && filtersPayload.selectedFeatureIds.length > 0) {
    filtersPayload.selectedFeatureIds.forEach(featureId => {
      urlSearchParams.append('features[]', String(featureId));
    });
  }

  if (filtersPayload.sellerId) {
    urlSearchParams.set('seller', String(filtersPayload.sellerId));
  } else {
    urlSearchParams.delete('seller');
  }

  return url.toString();
};


export const mountPublicOffers = (): void => {
  const offersContainer = document.getElementById('mp-offers') as HTMLElement | null;
  if (!offersContainer) {
    console.warn('Public offers container not found');
    return;
  }

  const apiEndpointUrl = offersContainer.dataset.apiPublicOffers || '';
  const basePageUrl = offersContainer.dataset.baseUrl || window.location.pathname;
  
  const sidebarForm = document.getElementById('mp-offers-filters') as HTMLFormElement | null;
  const sortingForm = document.getElementById('mp-offers-sort') as HTMLFormElement | null;
  const contentArea = document.getElementById('mp-offers-content') as HTMLElement | null;

  if (!apiEndpointUrl || !sidebarForm || !contentArea) {
    console.warn('Required elements for public offers filtering not found');
    return;
  }

  const executeFiltering = async (filtersPayload: OffersFiltersPayload): Promise<void> => {
    contentArea.classList.add('opacity-50');

    try {
      const updatedHtmlContent = await fetchFilteredOffersFromServer(apiEndpointUrl, filtersPayload);

      contentArea.innerHTML = updatedHtmlContent;

      const updatedUrl = buildUrlWithCleanParameters(basePageUrl, filtersPayload);
      window.history.replaceState({}, '', updatedUrl);
      
    } catch (error) {
      console.error('Failed to filter offers:', error);
    } finally {
      contentArea.classList.remove('opacity-50');
    }
  };

  const combineAllFilters = (): OffersFiltersPayload => {
    const sidebarFilters = readFiltersFromSidebarForm(sidebarForm);
    const sortingPreferences = sortingForm ? readSortingPreferencesFromForm(sortingForm) : {};
    
    return {
      ...sidebarFilters,
      ...sortingPreferences,
      currentPage: 1
    };
  };

  sidebarForm.addEventListener('submit', (event) => {
    event.preventDefault();
    const combinedFilters = combineAllFilters();
    executeFiltering(combinedFilters);
  });

  const featureCheckboxes = Array.from(
    sidebarForm.querySelectorAll<HTMLInputElement>('input[name="features[]"]')
  );
  
  featureCheckboxes.forEach(checkbox => {
    checkbox.addEventListener('change', () => {
      const combinedFilters = combineAllFilters();
      executeFiltering(combinedFilters);
    });
  });

  const categoryDropdown = sidebarForm.querySelector<HTMLSelectElement>('select[name="category"]');
  if (categoryDropdown) {
    categoryDropdown.addEventListener('change', () => {
      const combinedFilters = combineAllFilters();
      executeFiltering(combinedFilters);
    });
  }

  FiltersRangeSliders.init();

  const rangeSliderContainer = document.querySelector('.js-input-range-slider-container');
  if (rangeSliderContainer) {
    const rangeInputs = rangeSliderContainer.querySelectorAll<HTMLInputElement>('input[data-action^="range-"]');
    rangeInputs.forEach(input => {
      input.addEventListener('change', () => {
        const combinedFilters = combineAllFilters();
        executeFiltering(combinedFilters);
      });
    });
  }

  sidebarForm.addEventListener('marketplaceFilterChange', (event: any) => {
    if (event.detail?.source === 'priceSlider') {
      const combinedFilters = combineAllFilters();
      executeFiltering(combinedFilters);
    }
  });

  if (sortingForm) {
    sortingForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const combinedFilters = combineAllFilters();
      executeFiltering(combinedFilters);
    });
  }

  offersContainer.addEventListener('click', (event) => {
    const clickedElement = event.target as HTMLElement;
    const paginationLink = clickedElement.closest('a');
    
    if (!paginationLink) return;

    const isPaginationLink = paginationLink.closest('.pagination');
    if (isPaginationLink) {
      event.preventDefault();

      const linkUrl = new URL(paginationLink.href, window.location.origin);
      const pageNumber = Number(linkUrl.searchParams.get('page') || '1');
      
      const currentFilters = combineAllFilters();
      currentFilters.currentPage = pageNumber;
      
      executeFiltering(currentFilters);
    }
  });
};
